import { useNow, whenever } from '@vueuse/core';
import { acceptHMRUpdate, defineStore } from 'pinia';
import type { AuthenticateRequest, AuthenticateResponse, AuthVerifyRequest } from '~/types/ecommerce';
import type { JwtPayload } from 'jwt-decode';

type Role = 'customer' | 'guest';

interface AccessTokenPayload extends JwtPayload {
  email: string;
  cart_id: string;
  role: Role;
}

const AccessTokenKey = 'kygunco_access_token';
const RefreshTokenKey = 'kygunco_refresh_token';

/**
 * Pinia store for authentication state and logic.
 *
 * Manages login, logout, token storage (access & refresh tokens),
 * JWT decoding, token expiration tracking, and automatic refresh.
 *
 * Works with secure HTTP-only cookies for tokens.
 *
 * Integrates with Nuxt app's ecommerce API and event bus,
 * and uses Quasar for notifications.
 */
export const useAuthStore = defineStore('auth', () => {
  /**
   * Access token stored as secure cookie.
   * Automatically expires after 30 minutes.
   */
  const accessToken = useCookie(AccessTokenKey, {
    secure: true,
    sameSite: 'lax',
    maxAge: DAY_IN_SECONDS * 30,
  });

  /**
   * Refresh token stored as secure cookie.
   * Automatically expires after 30 days.
   */
  const refreshToken = useCookie(RefreshTokenKey, {
    secure: true,
    sameSite: 'lax',
    maxAge: DAY_IN_SECONDS * 30,
  });

  const { $ecommerce, $bus } = useNuxtApp();
  const { notify } = useQuasar();

  /** Reactive boolean indicating if an auth API request is pending */
  const pending = ref(false);

  /** Reactive current time, updating every second */
  const now = useNow({ interval: 1000 });

  /**
   * Login method - authenticates the user with backend,
   * setting the tokens and syncing guest cart if applicable.
   *
   * @param request - Login credentials and optional cartId
   * @returns Promise resolving when login attempt completes
   */
  const login = async (request: AuthenticateRequest) => {
    const cartStore = useCartStore();

    // Attach guest cartId if exists
    request.cartId = cartStore.guestCartId;

    try {
      const response = await $ecommerce.fetch<AuthenticateResponse>(
        'auth/authenticate',
        {
          method: 'POST',
          body: request,
          onRequest: () => { pending.value = true; },
          onResponse: () => { pending.value = false; },
        },
      );

      // Clear guest cart id on successful login to merge carts
      cartStore.guestCartId = null;

      // Store tokens in secure cookies
      accessToken.value = response.accessToken;
      refreshToken.value = response.refreshToken;
    }
    catch (error) {
      if (isFetchError(error) && error.status == 401) {
        notify({
          type: 'negative',
          message: 'Invalid username or password. Please check your credentials and try again.',
        });

        return;
      }

      handleFetchError(error);
    }
  };

  /**
   * Logs out the current user by clearing tokens,
   * notifying the user, and optionally navigating away.
   */
  const logout = async () => {
    accessToken.value = undefined;
    refreshToken.value = undefined;

    notify({
      type: 'positive',
      message: 'You have been successfully logged out. Hope to see you soon!',
    });

    await nextTick();
  };

  const guestify = async (request: AuthVerifyRequest) => {
    try {
      const response = await $ecommerce.fetch<AuthenticateResponse>(
        'auth/verify',
        {
          method: 'POST',
          body: request,
          onRequest: () => { pending.value = true; },
          onResponse: () => { pending.value = false; },
        },
      );

      // Store tokens in secure cookies
      accessToken.value = response.accessToken;
    }
    catch (error) {
      handleFetchError(error);
    }
  };

  /**
   * Refreshes the access token using the refresh token.
   * Updates the access token cookie or clears tokens on failure.
   */
  const refresh = async () => {
    try {
      const response = await $ecommerce.fetch<AuthenticateResponse>(
        'auth/refresh',
        {
          method: 'POST',
          body: { refreshToken: refreshToken.value },
          onRequest: () => { pending.value = true; },
          onResponse: () => { pending.value = false; },
        },
      );

      // Update access token cookie
      accessToken.value = response.accessToken;
    }
    catch (error) {
      if (isFetchError(error) && error.status == 401) {
        notify({
          type: 'negative',
          message: `Your current session is expires in ${refreshTokenExpiresIn.value} seconds.`,
        });

        return;
      }

      handleFetchError(error);
    }
  };

  /**
   * Decoded access token payload (typed).
   * Reactive computed property updated automatically.
   */
  const decodedAccessToken = useJwt<AccessTokenPayload>(() => accessToken.value ?? '');

  /**
   * Decoded refresh token payload.
   */
  const decodedRefreshToken = useJwt(() => refreshToken.value ?? '');

  /**
   * Timestamp when access token expires.
   */
  const accessTokenExpiresAt = computed(
    () => new Date((decodedAccessToken.payload.value?.exp ?? 0) * 1000),
  );

  /**
   * Number of seconds until access token expires.
   */
  const accessTokenExpiresIn = computed(
    () => Math.floor((accessTokenExpiresAt.value.getTime() - now.value.getTime()) / 1000),
  );

  /**
   * Whether the access token has expired.
   */
  const accessTokenExpired = computed(() => accessTokenExpiresIn.value <= 0);

  /**
   * Timestamp when refresh token expires.
   */
  const refreshTokenExpiresAt = computed(
    () => new Date((decodedRefreshToken.payload.value?.exp ?? 0) * 1000),
  );

  /**
   * Number of seconds until refresh token expires.
   */
  const refreshTokenExpiresIn = computed(
    () => Math.floor((refreshTokenExpiresAt.value.getTime() - now.value.getTime()) / 1000),
  );

  /**
   * Whether the refresh token has expired.
   */
  const refreshTokenExpired = computed(() => refreshTokenExpiresIn.value <= 0);

  /**
   * Whether the token should be refreshed soon.
   * True if logged in and access token expires in ≤ 10 minutes.
   */
  const shouldRefreshToken = computed(
    () => !refreshTokenExpired.value && accessTokenExpiresIn.value <= MINUTE_IN_SECONDS * 10,
  );

  /**
   * Whether the user is logged in.
   * True if access token role is 'customer' and token is not expired.
   */
  const isLoggedIn = computed(() => role.value == 'customer' && !accessTokenExpired.value);

  const isGuest = computed(() => role.value == 'guest' && !accessTokenExpired.value);

  const isValid = computed(() => isLoggedIn.value || isGuest.value);

  /**
   * Cart ID from decoded access token.
   */
  const cartId = computed(() => decodedAccessToken.payload.value?.cart_id);

  /**
   * Role from the access token.
   */
  const role = computed(() => decodedAccessToken.payload.value?.role);

  /**
   * Email from the access token.
   */
  const email = computed(() => decodedAccessToken.payload.value?.email);

  /**
   * User ID from the access token.
   */
  const userId = computed(() => decodedAccessToken.payload.value?.sub);

  /**
   * Watches when token should refresh and triggers refresh automatically.
   */
  const refreshWatchHandle = whenever(
    () => shouldRefreshToken.value && !refreshTokenExpired.value,
    async () => await refresh(),
    { immediate: true },
  );

  /**
   * Stops token refresh watcher when refresh token expires.
   * Removes refresh token cookie.
   */
  whenever(refreshTokenExpired, () => {
    refreshWatchHandle.stop();
    refreshToken.value = undefined;
  });

  /**
   * Removes access token cookie when access token expires.
   */
  whenever(accessTokenExpired, () => accessToken.value = undefined);

  /**
   * Emits auth login/logout events on auth status change.
   */
  watch(isLoggedIn, status => $bus.emit(`auth:${status ? 'login' : 'logout'}`));

  return {
    pending: readonly(pending),
    accessToken,
    refreshToken,
    cartId,
    role,
    email,
    userId,
    login,
    logout,
    guestify,
    isLoggedIn,
    isGuest,
    isValid,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot));
}
